'use client';

import { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useSubmitFeedback } from "@/hooks/useFeedback";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import { ArrowLeft, Mail, Send, Calendar, MessageCircle } from "lucide-react";

export default function ContactPage() {
  const [email, setEmail] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [title, setTitle] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [submitSuccess, setSubmitSuccess] = useState<boolean>(false);
  
  const { user } = useAuth();
  const submitFeedbackMutation = useSubmitFeedback();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim() || !name.trim() || !title.trim() || !description.trim()) {
      return;
    }

    try {
      const feedbackData = {
        user_id: user?.uid || 'anonymous',
        email: email.trim(),
        title: title.trim(),
        description: description.trim(),
        form_type: 'support' as const,
      };
      
      await submitFeedbackMutation.mutateAsync(feedbackData);
      
      setSubmitSuccess(true);
      setEmail("");
      setName("");
      setTitle("");
      setDescription("");
      setTimeout(() => setSubmitSuccess(false), 5000);
      
    } catch (error) {
    }
  };

  return (
    <div className="min-h-svh bg-background">
      {/* ---- Header ---- */}
      <header className="sticky top-0 z-30 flex items-center py-1.5 px-4 bg-background border-b border-border">
        <Link
          href="/"
          className="flex items-center gap-2 hover:opacity-80 transition-opacity"
        >
          <Image src="/logo.png" alt="Xynth" width={32} height={32} className="rounded-lg" />
        </Link>

        <nav className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 hidden md:flex items-center gap-6 text-[13px] font-normal text-zinc-500">
          <Link href="/blog" className="hover:text-white transition-colors duration-200">Use Cases</Link>
          <Link href="/pricing" className="hover:text-white transition-colors duration-200">Pricing</Link>
          <Link href="/community" className="hover:text-white transition-colors duration-200">Community</Link>
          <span className="text-white">Contact</span>
          <Link href="/careers" className="hover:text-white transition-colors duration-200">Careers</Link>
          <Link href="/about" className="hover:text-white transition-colors duration-200">About</Link>
        </nav>

        <div className="ml-auto flex items-center gap-2.5">
          <Link href="/login" className="h-7 px-3 text-xs text-zinc-400 hover:text-white inline-flex items-center transition-colors">
            Log in
          </Link>
          <Link href="/signup" className="h-7 px-3.5 text-xs font-medium bg-white text-black hover:bg-zinc-200 rounded-lg inline-flex items-center transition-colors">
            Try Free →
          </Link>
        </div>
      </header>

      <div className="mx-auto max-w-3xl px-5 py-16 md:py-24">
        {/* ---- Page Title ---- */}
        <div className="text-center mb-12">
          <h1 className="text-3xl font-semibold tracking-tight mb-3">Get in Touch</h1>
          <p className="text-[15px] text-zinc-400">
            Reach us directly or send a message below.
          </p>
        </div>

        {/* ---- Direct Contact Cards ---- */}
        <div className="grid gap-3 sm:grid-cols-3 mb-10">
          <a
            href="mailto:hashims@xynth.finance"
            className="flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3 hover:border-zinc-600 transition-colors"
          >
            <Mail className="size-4 text-zinc-400 shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium">Email</p>
              <p className="text-[12px] text-zinc-500 truncate">hashims@xynth.finance</p>
            </div>
          </a>

          <a
            href="https://calendly.com/syedhashim2015/30min"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3 hover:border-zinc-600 transition-colors"
          >
            <Calendar className="size-4 text-zinc-400 shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium">Book a Meeting</p>
              <p className="text-[12px] text-zinc-500">30 min with the founder</p>
            </div>
          </a>

          <a
            href="https://discord.gg/xynth"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3 hover:border-zinc-600 transition-colors"
          >
            <MessageCircle className="size-4 text-zinc-400 shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium">Discord</p>
              <p className="text-[12px] text-zinc-500">Join the community</p>
            </div>
          </a>
        </div>

        {/* ---- Social Links ---- */}
        <div className="flex items-center justify-center gap-6 mb-12">
          <a
            href="https://x.com/Hashim__Syed"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors"
          >
            <svg className="size-4" viewBox="0 0 24 24" fill="currentColor"><path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z"/></svg>
            @Hashim__Syed
          </a>
          <a
            href="https://x.com/Mutchtaba2"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors"
          >
            <svg className="size-4" viewBox="0 0 24 24" fill="currentColor"><path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z"/></svg>
            @Mutchtaba2
          </a>
        </div>

        {/* ---- Divider ---- */}
        <div className="flex items-center gap-4 mb-12">
          <div className="h-px flex-1 bg-border" />
          <span className="text-xs text-zinc-500 uppercase tracking-widest">or send a message</span>
          <div className="h-px flex-1 bg-border" />
        </div>

        {/* ---- Success Message ---- */}
        {submitSuccess && (
          <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 flex items-center gap-3 mb-6">
            <div className="bg-green-500/20 rounded-full p-2">
              <Send className="size-5 text-green-400" />
            </div>
            <div>
              <h3 className="font-semibold text-green-100">Message Sent!</h3>
              <p className="text-sm text-green-200">
                Thank you for reaching out. We&apos;ll get back to you shortly.
              </p>
            </div>
          </div>
        )}

        {/* ---- Contact Form ---- */}
        <form onSubmit={handleSubmit} className="space-y-5 bg-card border border-border rounded-lg p-6 md:p-8">
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                type="text"
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="title">Subject *</Label>
            <Input
              id="title"
              type="text"
              placeholder="What would you like to know about?"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Message *</Label>
            <Textarea
              id="description"
              placeholder="Tell us about your interest in Xynth, ask questions about our platform, or share any inquiries you have."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
              className="min-h-[140px] resize-none"
            />
            <p className="text-xs text-muted-foreground">
              {description.length}/1000 characters
            </p>
          </div>

          {submitFeedbackMutation.isError && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 text-sm text-destructive">
              {submitFeedbackMutation.error?.message || "Failed to submit. Please try again."}
            </div>
          )}

          <Button
            type="submit"
            className="w-full"
            size="lg"
            disabled={
              submitFeedbackMutation.isPending || 
              !email.trim() || 
              !name.trim() ||
              !title.trim() || 
              !description.trim()
            }
          >
            {submitFeedbackMutation.isPending ? (
              <span className="animate-pulse">Sending...</span>
            ) : (
              <>
                <Send className="size-4 mr-2" />
                Send Message
              </>
            )}
          </Button>
        </form>

        {/* ---- Back to Home ---- */}
        <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground mt-10">
          <Link 
            href="/" 
            className="flex items-center gap-1 hover:text-foreground transition-colors underline underline-offset-4"
          >
            <ArrowLeft className="size-4" />
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}
